import { CaretDown, CaretUp, ChevronRight } from '@carbon/icons-react'
import React, { useMemo, useState } from 'react'
import { generateColors, paginate } from '../../utils/CommonUtils'

import { DDSTypography } from 'den-design-system'
import NoRecord from '../../components/common/NoContent/NoRecord'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import SFLicenseDistributionCard from '../../components/SalesforceAudit/SFLicenseDistributionCard'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Table from '../../components/common/DDS/Table/Table'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import UsageBar from '../../components/SalesforceAudit/UsageBar'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import { timeFormats } from '../../enum/TIME'
import { useNavigate } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

const PermissionSets: React.FC = () => {
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)
  const { salesforceAuditStore } = useStoreContext()
  const totalLicenses = salesforceAuditStore.getTotalPermissionSetsLicenses()
  const usedLicenses = salesforceAuditStore.getUsedPermissionSetsLicenses()
  const mostCommonLicense =
    salesforceAuditStore.getMostCommonPermissionSetsLicenses()
  const distributions =
    salesforceAuditStore.getPermissionSetsLicensesDistribution()
  const data = salesforceAuditStore.getPermissionSetsLicenses()

  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })

  const globalColorMapping = useMemo(() => {
    const allDistributions =
      salesforceAuditStore.getPermissionSetsLicensesDistribution()
    const colors = generateColors(allDistributions.length)
    const mapping = new Map()

    const sortedDistributions = [...allDistributions].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    )

    sortedDistributions.forEach((d, i) => {
      if (d.name && !mapping.has(d.name)) {
        mapping.set(d.name, colors[i % colors.length])
      }
    })

    return mapping
  }, [])

  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const filteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (!trimmedSearch) return data

    const normalizedSearchColumn = searchColumn?.toLowerCase() || 'all'

    return data.filter((item: any) => {
      const name = (item?.MasterLabel || '').toLowerCase()
      const total = item?.TotalLicenses?.toString() || ''
      const used = item?.UsedLicenses?.toString() || ''
      const available =
        item?.TotalLicenses && item?.UsedLicenses
          ? (item.TotalLicenses - item.UsedLicenses).toString()
          : ''
      const usage =
        item?.TotalLicenses && item?.UsedLicenses
          ? ((item.UsedLicenses / item.TotalLicenses) * 100)
              .toFixed(2)
              .toString()
          : ''
      const status = (item?.Status || '').toLowerCase()

      // Format dates in multiple ways for flexible matching
      let formattedDates: string[] = []
      if (item?.LastModifiedDate) {
        const m = moment(item.LastModifiedDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(),
          m.format('D MMMM YYYY').toLowerCase(),
          m.format('MMM D').toLowerCase(),
          m.format('D MMM').toLowerCase(),
          m.format('MMMM D').toLowerCase(),
          m.format('D MMMM').toLowerCase(),
          m.format('YYYY').toLowerCase(),
          m.format('MMMM').toLowerCase(),
          m.format('D').toLowerCase()
        ]
      }

      const matchesDate = formattedDates.some((date) =>
        date.includes(trimmedSearch)
      )

      switch (normalizedSearchColumn) {
        case 'masterlabel':
          return name.includes(trimmedSearch)
        case 'totallicenses':
          return total.includes(trimmedSearch)
        case 'usedlicenses':
          return used.includes(trimmedSearch)
        case 'available':
          return available.includes(trimmedSearch)
        case 'status':
          return status.includes(trimmedSearch)
        case 'lastmodifieddate':
          return matchesDate
        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            total.includes(trimmedSearch) ||
            used.includes(trimmedSearch) ||
            available.includes(trimmedSearch) ||
            status.includes(trimmedSearch) ||
            matchesDate
          )
      }
    })
  }, [data, activeSearchTerm, searchColumn])

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData
    return [...filteredData].sort((a: any, b: any) => {
      if (sortConfig.key === 'Id') {
        const licenseA = salesforceAuditStore.getLicenseById(a.Id) || 0
        const licenseB = salesforceAuditStore.getLicenseById(b.Id) || 0

        const availableA =
          (licenseA?.TotalLicenses || 0) - (licenseA?.UsedLicenses || 0)
        const availableB =
          (licenseB?.TotalLicenses || 0) - (licenseB?.UsedLicenses || 0)

        return sortConfig.direction === 'asc'
          ? availableA - availableB
          : availableB - availableA
      }

      if (sortConfig.key === 'LastModifiedDate') {
        const dateA = a.LastModifiedDate
          ? new Date(a.LastModifiedDate).getTime()
          : 0
        const dateB = b.LastModifiedDate
          ? new Date(b.LastModifiedDate).getTime()
          : 0
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA
      }
      const valA = a?.[sortConfig.key!] || ''
      const valB = b?.[sortConfig.key!] || ''
      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return sortConfig.direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA)
      }
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
      return 0
    })
  }, [filteredData, sortConfig, salesforceAuditStore])

  const paginatedPermissionSetLicensesData = paginate(
    sortedData,
    currentPage,
    SalesforceAuditStore.pageSize
  )

  const Columns = [
    {
      columnHeaderStyle: { width: '300px' },
      columnDataStyle: { width: '300px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Permission Set Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('MasterLabel')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'MasterLabel' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'MasterLabel' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'MasterLabel',
      render: (text: any) => (
        <div className='flex justify-start max-w-[180px] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 18 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Usage
            </DDSTypography.Paragraph>
          </div>
        </div>
      ),
      dataLabel: 'Id',
      render: (item: any) => {
        const license = salesforceAuditStore.getPermissionSetsLicensesById(item)
        const usage =
          license.TotalLicenses > 0
            ? +((license.UsedLicenses / license.TotalLicenses) * 100).toFixed(2)
            : 0

        return (
          <div className='flex items-start justify-start gap-[8px] min-w-[200px]'>
            <div className='w-[150px]'>
              <UsageBar usage={usage} />
            </div>
            <div className='w-[50px]'>
              <DDSTypography.Paragraph
                size='caption'
                variant='semiBold'
                color={pallete.colors.textDark6}
              >
                {usage}%
              </DDSTypography.Paragraph>
            </div>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Usage Nos.
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('UsedLicenses')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'UsedLicenses' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'UsedLicenses' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'UsedLicenses', // used for sorting or default fallback
      render: (text: any, row: any) => {
        const used = Number(row?.UsedLicenses)
        const total = Number(row?.TotalLicenses)

        const isFullyUsed = used > 0 && used === total

        const textColor = isFullyUsed
          ? pallete.colors.warningRed2
          : pallete.colors.textDark4

        return (
          <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
            <div className='flex justify-start items-start space-x-1 text-[14px] font-medium'>
              <div style={{ color: textColor }}>{used.toLocaleString()}</div>
              <div style={{ color: textColor }}>/ {total.toLocaleString()}</div>
            </div>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Status
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Status')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Status' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Status' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Status',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            style={{
              color:
                text === 'Active'
                  ? pallete.colors.primary2
                  : pallete.colors.warningRed2
            }}
          >
            {text || '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Modified
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedDate',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph size='para' variant='medium'>
            {text
              ? moment(text).format(timeFormats.shortMonthDateYearTime)
              : '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    {
      columnHeaderStyle: { width: '20px' },
      columnDataStyle: { width: '20px' },
      heading: '',
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='cursor-pointer'>
            <ChevronRight
              size={16}
              onClick={() => {
                navigate(`${text}?tab=2`)
              }}
              color={pallete.colors.FillButton}
            />
          </div>
        )
      }
    }
  ]

  return (
    <div className='flex flex-col w-full py-[24px]'>
      <div className='relative pb-[12px]'>
        <Search
          id='search'
          name='Search'
          placeholder='Search'
          value={searchTerm}
          onChange={(e) => {
            const value = e.target.value
            setSearchTerm(value)
            setShowDropdown(value.length > 0)
            if (value.trim() === '') {
              setActiveSearchTerm('')
            }
          }}
          autoComplete='off'
          width='100%'
          onPressEnter={() => {
            setActiveSearchTerm(searchTerm.trim())
            setCurrentPage(1)
            setShowDropdown(false)
          }}
          onBlur={() => {
            setTimeout(() => {
              setShowDropdown(false)
              setCurrentPage(1)
            }, 200)
          }}
        />

        {showDropdown && (
          <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
            {[
              { label: 'License Name', value: 'Name' },

              { label: 'Status', value: 'Status' },
              { label: 'Last Modified', value: 'LastModifiedDate' },
              { label: 'All Columns', value: 'all' }
            ].map((option) => (
              <div
                key={option.value}
                onClick={() => {
                  setSearchColumn(option.value)
                  setCurrentPage(1)
                  setActiveSearchTerm(searchTerm)
                  setShowDropdown(false)
                }}
                className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
              >
                <span className='font-mulish text-black'>{searchTerm}</span>
                <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                  in: {option.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className='flex flex-col pt-[12px]'>
        <SFLicenseDistributionCard
          usage={(usedLicenses / totalLicenses) * 100}
          mostCommonLicense={mostCommonLicense?.MasterLabel || ''}
          totalLicenses={totalLicenses}
          usedLicenses={usedLicenses}
          distributions={distributions}
          colorMapping={globalColorMapping}
        />
      </div>
      <div className='flex flex-col w-full pt-[24px]'>
        {sortedData?.length > 0 ? (
          <Table
            columns={Columns}
            data={paginatedPermissionSetLicensesData}
            totalCount={sortedData?.length}
            currentPage={currentPage}
            rowsPerPage={SalesforceAuditStore.pageSize}
            onPageChange={(page) => setCurrentPage(page)}
            clickable={(id) => {
              navigate(`${id}?tab=2`)
            }}
            clickType='SalesforceLicenses'
          />
        ) : searchTerm?.trim() ? (
          <NoRecord
            imageSrc={NoRecordImage}
            style={{ height: '45vh' }}
            text={<div>No Licenses match your search input.</div>}
          />
        ) : (
          <NoRecord
            imageSrc={NoRecordData}
            style={{ height: '45vh' }}
            text={<div>No User Licenses are currently assigned.</div>}
          />
        )}
      </div>
    </div>
  )
}

export default observer(PermissionSets)
